import { useEffect, useImperativeHandle, useRef, useState, forwardRef } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import DownloadIcon from '@mui/icons-material/Download';
import SaveIcon from '@mui/icons-material/Save';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import axiosConfig from 'src/Resources/axiosConfig';
import DailySection from './DailySection';

interface WeeklyReportProps {
  installationId: number;
  installationName?: string;
  installationEmail?: string;
}

interface DailyEnergyData {
  date: string;
  pvProduction: number;
  loadConsumption: number;
  gridImport: number;
  gridExport: number;
  batteryCharged: number;
  batteryDischarged: number;
}

interface WeeklySummary {
  totalPvProduction: number;
  totalConsumption: number;
  totalGridImport: number;
  totalGridExport: number;
  totalBatteryCharged: number;
  totalBatteryDischarged: number;
}

interface WeeklyReportResponse {
  installationName: string;
  periodStart: string;
  periodEnd: string;
  currentWeek: WeeklySummary;
  previousWeek: WeeklySummary | null;
  totalEnergySaved: number;
  totalSavingsCHF: number;
  daysEquivalent: number;
  selfSufficiencyPercent: number;
  selfConsumptionPercent: number;
  batteryEfficiencyPercent: number;
  gridDependencyPercent: number;
  pvChangePercent: number;
  consumptionChangePercent: number;
  gridImportChangePercent: number;
  dailyData: DailyEnergyData[];
  aiInsight: string;
  daysAvailable: number;
  daysExpected: number;
  missingDates: string[];
}

interface ReportSummary {
  installationId: number;
  periodStart: string;
  periodEnd: string;
  totalPvProduction: number;
  totalConsumption: number;
  totalGridImport: number;
  totalGridExport: number;
  totalBatteryCharged: number;
  totalBatteryDischarged: number;
  totalEnergySaved: number;
  totalSavingsCHF: number;
  selfSufficiencyPercent: number;
  selfConsumptionPercent: number;
  batteryEfficiencyPercent: number;
  gridDependencyPercent: number;
  aiInsight: string;
}

interface MonthlyReport extends ReportSummary {
  year: number;
  month: number;
  weekCount: number;
  avgPeakLoadHour: number;
  avgPeakSolarHour: number;
  avgWeekdayDailyLoad: number;
  avgWeekendDailyLoad: number;
  isPreview?: boolean;
  daysAvailable?: number;
  daysInMonth?: number;
}

interface YearlyReport extends ReportSummary {
  year: number;
  monthCount: number;
  avgPeakLoadHour: number;
  avgPeakSolarHour: number;
  avgWeekdayDailyLoad: number;
  avgWeekendDailyLoad: number;
  isPreview?: boolean;
}

interface WeeklyReportSummaryRecord {
  id: number;
  installationId: number;
  periodStart: string;
  periodEnd: string;
  totalPvProduction: number;
  totalConsumption: number;
  totalGridImport: number;
  totalGridExport: number;
  totalBatteryCharged: number;
  totalBatteryDischarged: number;
  totalEnergySaved: number;
  totalSavingsCHF: number;
  selfSufficiencyPercent: number;
  selfConsumptionPercent: number;
  batteryEfficiencyPercent: number;
  gridDependencyPercent: number;
  peakLoadHour: number;
  peakSolarHour: number;
  weekdayAvgDailyLoad: number;
  weekendAvgDailyLoad: number;
  aiInsight: string;
  createdAt: string;
}

// Matches: time ranges (14:00–18:00), times (09:00), decimals (126.4 / 1,3), integers (34)
const BOLD_PATTERN = /(\d{1,2}:\d{2}(?:[–\-]\d{1,2}:\d{2})?|\d+[.,]\d+|\d+)/g;
const isBold = (s: string) => /\d/.test(s);

function FormattedBullet({ text }: { text: string }) {
  const colonIdx = text.indexOf(':');
  if (colonIdx > 0) {
    const title = text.slice(0, colonIdx);
    const rest  = text.slice(colonIdx + 1);
    const restParts = rest.split(BOLD_PATTERN).map((p, i) =>
      isBold(p) ? <strong key={i}>{p}</strong> : <span key={i}>{p}</span>
    );
    return <><strong>{title}</strong>:{restParts}</>;
  }
  const parts = text.split(BOLD_PATTERN).map((p, i) =>
    isBold(p) ? <strong key={i}>{p}</strong> : <span key={i}>{p}</span>
  );
  return <>{parts}</>;
}

function getMonthName(month: number, locale: string): string {
  const date = new Date(2000, month - 1, 1);
  return date.toLocaleDateString(locale, { month: 'long' });
}

// ── Email Bar (shared) ──────────────────────────────────────────

function EmailBar({ onSend, disabled }: { onSend: (email: string) => Promise<void>; disabled?: boolean }) {
  const intl = useIntl();
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sendStatus, setSendStatus] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const handleSend = async () => {
    if (!email.trim()) return;
    setSending(true);
    try {
      await onSend(email.trim());
      setSendStatus({ message: intl.formatMessage({ id: 'reportSentTo' }, { email }), severity: 'success' });
    } catch {
      setSendStatus({ message: intl.formatMessage({ id: 'reportSendError' }), severity: 'error' });
    } finally {
      setSending(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', mb: 3, gap: 0.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TextField
          size="small"
          placeholder="recipient@example.com"
          value={email}
          onChange={(e) => { setEmail(e.target.value); setSendStatus(null); }}
          sx={{ width: 280 }}
          onKeyDown={(e) => e.key === 'Enter' && handleSend()}
        />
        <Button
          variant="contained"
          startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
          onClick={handleSend}
          disabled={sending || !email.trim() || disabled}
          sx={{ bgcolor: '#2c3e50', '&:hover': { bgcolor: '#34495e' } }}
        >
          <FormattedMessage id="sendReport" defaultMessage="Send Report" />
        </Button>
      </Box>
      {sendStatus && (
        <Typography variant="caption" sx={{ color: sendStatus.severity === 'success' ? '#27ae60' : '#e74c3c' }}>
          {sendStatus.message}
        </Typography>
      )}
    </Box>
  );
}

// ── Main Component ─────────────────────────────────────────────

function WeeklyReport({ installationId, installationName, installationEmail }: WeeklyReportProps) {
  const intl = useIntl();
  const [activeTab, setActiveTab] = useState(0);
  const [monthlyReports, setMonthlyReports] = useState<MonthlyReport[]>([]);
  const [yearlyReports, setYearlyReports] = useState<YearlyReport[]>([]);
  const [monthlyPreview, setMonthlyPreview] = useState<MonthlyReport | null>(null);
  const [yearlyPreview, setYearlyPreview] = useState<YearlyReport | null>(null);
  const [selectedMonthlyIdx, setSelectedMonthlyIdx] = useState(0);
  const [selectedYearlyIdx, setSelectedYearlyIdx] = useState(0);
  const [dailyHasData, setDailyHasData] = useState(false);
  const [weeklyHasData, setWeeklyHasData] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [reportPeriods, setReportPeriods] = useState<Record<string, { start: string; end: string; year?: number; month?: number }>>({});
  const weeklyRef = useRef<WeeklySectionHandle>(null);

  // Auto-send email preferences
  const [autoSend, setAutoSend] = useState({ sendWeekly: false, sendMonthly: false, sendYearly: false });
  const [autoSendDirty, setAutoSendDirty] = useState(false);
  const [savingAutoSend, setSavingAutoSend] = useState(false);
  const [autoSendSnackbar, setAutoSendSnackbar] = useState<string | null>(null);

  useEffect(() => {
    axiosConfig.get('/GetEmailPreference', { params: { installationId } })
      .then(res => {
        setAutoSend({ sendWeekly: res.data.sendWeekly, sendMonthly: res.data.sendMonthly, sendYearly: res.data.sendYearly });
        setAutoSendDirty(false);
      })
      .catch(() => {});
  }, [installationId]);

  const handleAutoSendChange = (field: 'sendWeekly' | 'sendMonthly' | 'sendYearly') => {
    setAutoSend(prev => ({ ...prev, [field]: !prev[field] }));
    setAutoSendDirty(true);
  };

  const handleSaveAutoSend = async () => {
    setSavingAutoSend(true);
    try {
      await axiosConfig.post('/UpdateEmailPreference', null, {
        params: { installationId, ...autoSend }
      });
      setAutoSendDirty(false);
      setAutoSendSnackbar(intl.formatMessage({ id: 'autoSendSaved', defaultMessage: 'Auto-send preferences saved.' }));
    } catch {
      setAutoSendSnackbar(intl.formatMessage({ id: 'autoSendSaveFailed', defaultMessage: 'Failed to save auto-send preferences.' }));
    } finally {
      setSavingAutoSend(false);
    }
  };

  const hasEmail = !!(installationEmail && installationEmail.trim());

  const fetchReportData = () => {
    const lang = intl.locale;
    axiosConfig.get('/GetMonthlyReports', { params: { installationId, language: lang } })
      .then(res => setMonthlyReports(res.data))
      .catch(() => {});
    axiosConfig.get('/GetYearlyReports', { params: { installationId, language: lang } })
      .then(res => setYearlyReports(res.data))
      .catch(() => {});
    axiosConfig.get('/GetCurrentMonthPreview', { params: { installationId, language: lang } })
      .then(res => setMonthlyPreview(res.data))
      .catch(() => setMonthlyPreview(null));
    axiosConfig.get('/GetCurrentYearPreview', { params: { installationId, language: lang } })
      .then(res => setYearlyPreview(res.data))
      .catch(() => setYearlyPreview(null));
  };

  useEffect(() => { fetchReportData(); }, [installationId, intl.locale]);

  const tabs = [
    { label: intl.formatMessage({ id: 'dailyTab', defaultMessage: 'Daily' }), key: 'daily' },
    { label: intl.formatMessage({ id: 'weeklyTab', defaultMessage: 'Weekly' }), key: 'weekly' },
    { label: intl.formatMessage({ id: 'monthlyTab', defaultMessage: 'Monthly' }), key: 'monthly' },
    { label: intl.formatMessage({ id: 'yearlyTab', defaultMessage: 'Yearly' }), key: 'yearly' }
  ];

  const safeTab = Math.min(activeTab, tabs.length - 1);

  const activeTabHasData = (() => {
    const key = tabs[safeTab]?.key;
    if (key === 'daily') return dailyHasData;
    if (key === 'weekly') return weeklyHasData;
    if (key === 'monthly') return monthlyReports.length > 0 || monthlyPreview !== null;
    if (key === 'yearly') return yearlyReports.length > 0 || yearlyPreview !== null;
    return false;
  })();

  const handleDownloadPdf = async () => {
    const reportType = tabs[safeTab]?.key ?? 'report';
    let endpoint = '';
    const params: Record<string, any> = { installationId, language: intl.locale };

    switch (reportType) {
      case 'daily':
        endpoint = '/GetDailyReportHtml';
        if (reportPeriods.daily?.start) params.date = reportPeriods.daily.start;
        break;
      case 'weekly':
        endpoint = '/GetWeeklyReportHtml';
        break;
      case 'monthly':
        endpoint = '/GetMonthlyReportHtml';
        if (reportPeriods.monthly?.year) params.year = reportPeriods.monthly.year;
        if (reportPeriods.monthly?.month) params.month = reportPeriods.monthly.month;
        break;
      case 'yearly':
        endpoint = '/GetYearlyReportHtml';
        if (reportPeriods.yearly?.year) params.year = reportPeriods.yearly.year;
        break;
    }

    if (!endpoint) return;

    setDownloadingPdf(true);
    try {
      const res = await axiosConfig.get(endpoint, { params, responseType: 'text' });
      const printWindow = window.open('', '_blank');
      if (!printWindow) return;

      const activePeriod = reportPeriods[reportType] ?? null;
      const dateRange = activePeriod
        ? `${activePeriod.start.replace(/-/g, '')}-${activePeriod.end.replace(/-/g, '')}`
        : new Date().toISOString().split('T')[0].replace(/-/g, '');

      const safeName = (installationName || String(installationId))
        .replace(/\s+/g, '-')
        .replace(/[^a-zA-Z0-9\-_]/g, '');

      printWindow.document.write(res.data);
      printWindow.document.close();
      printWindow.document.title = `${safeName}-${reportType}-${dateRange}`;
      printWindow.onafterprint = () => printWindow.close();
      const imgs = printWindow.document.querySelectorAll('img');
      const allLoaded = Array.from(imgs).map(img =>
        img.complete ? Promise.resolve() : new Promise<void>(r => { img.onload = () => r(); img.onerror = () => r(); })
      );
      Promise.all(allLoaded).then(() => setTimeout(() => printWindow.print(), 200));
    } catch (err) {
      console.error('PDF download failed', err);
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <Box sx={{ p: 2, width: '100%', maxWidth: 900, mx: 'auto' }} className="report-container">
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }} className="no-print">
        <Tabs
          value={safeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{ flex: 1, '& .MuiTab-root': { fontWeight: 'bold' } }}
        >
          {tabs.map(t => <Tab key={t.key} label={t.label} />)}
        </Tabs>
        {activeTabHasData && (
          <Button
            variant="outlined"
            startIcon={downloadingPdf ? <CircularProgress size={16} /> : <DownloadIcon />}
            onClick={handleDownloadPdf}
            disabled={downloadingPdf}
            sx={{ ml: 2, whiteSpace: 'nowrap' }}
          >
            <FormattedMessage id="downloadPdf" defaultMessage="Download PDF" />
          </Button>
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1, flexWrap: 'wrap' }} className="no-print">
        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#555' }}>
          <FormattedMessage id="autoSendReports" defaultMessage="Auto-send reports:" />
        </Typography>
        <Tooltip title={!hasEmail ? intl.formatMessage({ id: 'autoSendNoEmail', defaultMessage: 'Set email address in Information tab to enable auto-send' }) : ''}>
          <span>
            <FormControlLabel
              control={<Checkbox checked={autoSend.sendWeekly} onChange={() => handleAutoSendChange('sendWeekly')} disabled={!hasEmail} size="small" />}
              label={intl.formatMessage({ id: 'weeklyTab', defaultMessage: 'Weekly' })}
            />
          </span>
        </Tooltip>
        <Tooltip title={!hasEmail ? intl.formatMessage({ id: 'autoSendNoEmail', defaultMessage: 'Set email address in Information tab to enable auto-send' }) : ''}>
          <span>
            <FormControlLabel
              control={<Checkbox checked={autoSend.sendMonthly} onChange={() => handleAutoSendChange('sendMonthly')} disabled={!hasEmail} size="small" />}
              label={intl.formatMessage({ id: 'monthlyTab', defaultMessage: 'Monthly' })}
            />
          </span>
        </Tooltip>
        <Tooltip title={!hasEmail ? intl.formatMessage({ id: 'autoSendNoEmail', defaultMessage: 'Set email address in Information tab to enable auto-send' }) : ''}>
          <span>
            <FormControlLabel
              control={<Checkbox checked={autoSend.sendYearly} onChange={() => handleAutoSendChange('sendYearly')} disabled={!hasEmail} size="small" />}
              label={intl.formatMessage({ id: 'yearlyTab', defaultMessage: 'Yearly' })}
            />
          </span>
        </Tooltip>
        <Button
          variant="contained"
          size="small"
          startIcon={savingAutoSend ? <CircularProgress size={14} /> : <SaveIcon />}
          onClick={handleSaveAutoSend}
          disabled={!autoSendDirty || savingAutoSend}
          sx={{ ml: 1 }}
        >
          <FormattedMessage id="applyChanges" defaultMessage="Save" />
        </Button>
        {!hasEmail && (
          <Typography variant="caption" sx={{ color: '#e67e22', ml: 1 }}>
            <FormattedMessage id="autoSendNoEmail" defaultMessage="Set email address in Information tab to enable auto-send" />
          </Typography>
        )}
      </Box>

      <Snackbar
        open={!!autoSendSnackbar}
        autoHideDuration={4000}
        onClose={() => setAutoSendSnackbar(null)}
        message={autoSendSnackbar}
      />

      <Box sx={{ display: tabs[safeTab]?.key === 'daily' ? 'block' : 'none', minHeight: '50vh' }}>
        <DailySection installationId={installationId} onHasData={setDailyHasData} onPeriodChange={(date: string) => setReportPeriods(prev => ({ ...prev, daily: { start: date, end: date } }))} />
      </Box>
      <Box sx={{ display: tabs[safeTab]?.key === 'weekly' ? 'block' : 'none', minHeight: '50vh' }}>
        <WeeklySection
          ref={weeklyRef}
          installationId={installationId}
          latestMonthlyPeriodEnd={
            monthlyReports.length > 0
              ? monthlyReports.reduce((a, b) => a.periodEnd > b.periodEnd ? a : b).periodEnd
              : null
          }
          onHasData={setWeeklyHasData}
          onPeriodChange={(start, end) => setReportPeriods(prev => ({ ...prev, weekly: { start, end } }))}
        />
      </Box>
      <Box sx={{ display: tabs[safeTab]?.key === 'monthly' ? 'block' : 'none', minHeight: '50vh' }}>
        <MonthlySection
          installationId={installationId}
          reports={monthlyReports}
          preview={monthlyPreview}
          selectedIdx={selectedMonthlyIdx}
          onSelectedIdxChange={setSelectedMonthlyIdx}
          onPeriodChange={(r: MonthlyReport) => setReportPeriods(prev => ({ ...prev, monthly: { start: r.periodStart, end: r.periodEnd, year: r.year, month: r.month } }))}
        />
      </Box>
      <Box sx={{ display: tabs[safeTab]?.key === 'yearly' ? 'block' : 'none', minHeight: '50vh' }}>
        <YearlySection
          installationId={installationId}
          reports={yearlyReports}
          preview={yearlyPreview}
          selectedIdx={selectedYearlyIdx}
          onSelectedIdxChange={setSelectedYearlyIdx}
          onPeriodChange={(r: YearlyReport) => setReportPeriods(prev => ({ ...prev, yearly: { start: r.periodStart, end: r.periodEnd, year: r.year } }))}
        />
      </Box>
    </Box>
  );
}

// ── Weekly Section (existing weekly report content) ────────────

interface WeeklySectionHandle {
  regenerate: () => void;
}

const WeeklySection = forwardRef<WeeklySectionHandle, { installationId: number; latestMonthlyPeriodEnd: string | null; onHasData?: (hasData: boolean) => void; onPeriodChange?: (start: string, end: string) => void }>(
  ({ installationId, latestMonthlyPeriodEnd, onHasData, onPeriodChange }, ref) => {
  const intl = useIntl();
  const [report, setReport] = useState<WeeklyReportResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchReport();
  }, [installationId, intl.locale]);

  const fetchReport = async (forceRegenerate = false) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosConfig.get('/GetWeeklyReport', {
        params: { installationId, language: intl.locale, forceRegenerate }
      });
      setReport(res.data);
      onHasData?.(true);
      onPeriodChange?.(res.data.periodStart, res.data.periodEnd);
    } catch (err: any) {
      const msg =
        err.response?.data ||
        err.message ||
        intl.formatMessage({ id: 'failedToLoadReport' });
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
      onHasData?.(false);
    } finally {
      setLoading(false);
    }
  };

  useImperativeHandle(ref, () => ({ regenerate: () => fetchReport(true) }));

  const handleSendEmail = async (emailAddress: string) => {
    await axiosConfig.post('/SendWeeklyReportEmail', null, {
      params: { installationId, emailAddress }
    });
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          py: 6
        }}
      >
        <CircularProgress size={50} style={{ color: '#ffc04d' }} />
        <Typography variant="body2" mt={2}>
          <FormattedMessage id="generatingReport" defaultMessage="Generating weekly report..." />
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
        <Alert severity="warning">
          <FormattedMessage id="noReportData" defaultMessage="No report data found." />
        </Alert>
      </Box>
    );
  }

  if (!report) return null;

  const cur = report.currentWeek;
  const prev = report.previousWeek;

  const currentWeekDayCount  = Math.min(7, report.dailyData.length);
  const previousWeekDayCount = 7;

  const formatChange = (pct: number) =>
    pct === 0 ? '—' : pct > 0 ? `+${pct.toFixed(1)}%` : `${pct.toFixed(1)}%`;

  const changeColor = (pct: number, invert = false) => {
    const effective = invert ? -pct : pct;
    return effective > 0 ? '#27ae60' : effective < 0 ? '#e74c3c' : '#888';
  };

  // Compute change % from two raw values; returns 0 (shown as —) if prev is 0
  const calcChange = (curVal: number, prevVal: number) =>
    prevVal === 0 ? 0 : ((curVal - prevVal) / prevVal) * 100;

  const insightBullets = report.aiInsight
    .split(/\n+/)
    .map((line) => line.replace(/\*\*/g, '').replace(/^[\d]+[.)]\s*/, '').replace(/^[-*]\s*/, '').trim())
    .filter((line) => line.length > 0);

  const totalEnergySavedKwh = report.totalEnergySaved;
  const totalSavingsCHF     = report.totalSavingsCHF;

  const maxDailyValue = Math.max(
    ...report.dailyData.map((d) => Math.max(d.pvProduction, d.loadConsumption, d.gridImport)),
    1
  );

  return (
    <>
      {/* Email bar */}
      <EmailBar onSend={handleSendEmail} />

      {/* Report Header */}
      <Paper
        sx={{
          bgcolor: '#2c3e50',
          color: '#fff',
          p: 3,
          mb: 3,
          borderRadius: 2
        }}
      >
        <Typography variant="h5" fontWeight="bold">
          <FormattedMessage id="reportTitle" defaultMessage="Weekly Performance Report" />
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.9, mt: 0.5 }}>
          {report.installationName}
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.7 }}>
          {report.periodStart} — {report.periodEnd}
        </Typography>
      </Paper>

      {/* Missing days warning */}
      {report.missingDates && report.missingDates.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <FormattedMessage
            id="missingDaysWarning"
            defaultMessage="Data available for {available}/{expected} days. Missing: {dates}"
            values={{
              available: report.daysAvailable,
              expected: report.daysExpected,
              dates: report.missingDates.join(', ')
            }}
          />
        </Alert>
      )}

      {/* Weekly Insights */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: '#2c3e50' }}>
          <FormattedMessage id="weeklyInsights" defaultMessage="Weekly Insights" />
        </Typography>
        <InsightBox text={report.aiInsight} bullets={insightBullets} />
      </Paper>

      {/* Your Savings This Week */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: '#2c3e50' }}>
          <FormattedMessage id="weeklySavings" defaultMessage="Your Savings This Week" />
        </Typography>
        <SavingsCards
          intl={intl}
          energySaved={totalEnergySavedKwh}
          savingsCHF={totalSavingsCHF}
          selfSufficiency={report.selfSufficiencyPercent}
          batteryEfficiency={report.batteryEfficiencyPercent}
          hint={report.daysEquivalent > 0 ? `≈ ${report.daysEquivalent} ${intl.formatMessage({ id: 'daysOfYourUsage' })}` : undefined}
        />
      </Paper>

      {/* Weekly Summary Table */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: '#2c3e50' }}>
          <FormattedMessage id="weeklySummary" defaultMessage="Weekly Summary" />
        </Typography>
        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', '& td, & th': { p: 1.2, borderBottom: '1px solid #eee' } }}>
          <thead>
            <tr style={{ background: '#f8f9fa' }}>
              <th style={{ textAlign: 'left' }}><FormattedMessage id="metric" defaultMessage="Metric" /></th>
              <th style={{ textAlign: 'right' }}><FormattedMessage id="thisWeek" defaultMessage="This Week" /></th>
              {prev && <th style={{ textAlign: 'right' }}><FormattedMessage id="lastWeek" defaultMessage="Last Week" /></th>}
              {prev && <th style={{ textAlign: 'right' }}><FormattedMessage id="change" defaultMessage="Change" /></th>}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><FormattedMessage id="pvProduction" defaultMessage="PV Production" /></td>
              <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{cur.totalPvProduction.toFixed(1)} kWh</td>
              {prev && <td style={{ textAlign: 'right', color: '#888' }}>{prev.totalPvProduction.toFixed(1)} kWh</td>}
              {prev && <td style={{ textAlign: 'right', color: changeColor(report.pvChangePercent) }}>{formatChange(report.pvChangePercent)}</td>}
            </tr>
            <tr>
              <td><FormattedMessage id="consumption" defaultMessage="Consumption" /></td>
              <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{cur.totalConsumption.toFixed(1)} kWh</td>
              {prev && <td style={{ textAlign: 'right', color: '#888' }}>{prev.totalConsumption.toFixed(1)} kWh</td>}
              {prev && <td style={{ textAlign: 'right', color: changeColor(report.consumptionChangePercent, true) }}>{formatChange(report.consumptionChangePercent)}</td>}
            </tr>
            <tr style={{ background: '#fafafa' }}>
              <td style={{ color: '#888', paddingLeft: '20px', fontSize: '13px' }}>
                <FormattedMessage id="avgDailyConsumption" defaultMessage="Avg Daily Consumption" />
              </td>
              <td style={{ textAlign: 'right', color: '#888', fontSize: '13px' }}>
                {(cur.totalConsumption / currentWeekDayCount).toFixed(1)} kWh
              </td>
              {prev && <td style={{ textAlign: 'right', color: '#bbb', fontSize: '13px' }}>
                {(prev.totalConsumption / previousWeekDayCount).toFixed(1)} kWh
              </td>}
              {prev && <td />}
            </tr>
            <tr>
              <td><FormattedMessage id="gridImport" defaultMessage="Grid Import" /></td>
              <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{cur.totalGridImport.toFixed(1)} kWh</td>
              {prev && <td style={{ textAlign: 'right', color: '#888' }}>{prev.totalGridImport.toFixed(1)} kWh</td>}
              {prev && <td style={{ textAlign: 'right', color: changeColor(report.gridImportChangePercent, true) }}>{formatChange(report.gridImportChangePercent)}</td>}
            </tr>
            <tr>
              <td><FormattedMessage id="gridExport" defaultMessage="Grid Export" /></td>
              <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{cur.totalGridExport.toFixed(1)} kWh</td>
              {prev && <td style={{ textAlign: 'right', color: '#888' }}>{prev.totalGridExport.toFixed(1)} kWh</td>}
              {prev && <td style={{ textAlign: 'right', color: '#888' }}>{formatChange(calcChange(cur.totalGridExport, prev.totalGridExport))}</td>}
            </tr>
            <tr>
              <td><FormattedMessage id="batteryInOut" defaultMessage="Battery In / Out" /></td>
              <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{cur.totalBatteryCharged.toFixed(1)} / {cur.totalBatteryDischarged.toFixed(1)} kWh</td>
              {prev && <td style={{ textAlign: 'right', color: '#888' }}>{prev.totalBatteryCharged.toFixed(1)} / {prev.totalBatteryDischarged.toFixed(1)} kWh</td>}
              {prev && <td style={{ textAlign: 'right', color: changeColor(calcChange(cur.totalBatteryCharged, prev.totalBatteryCharged)) }}>{formatChange(calcChange(cur.totalBatteryCharged, prev.totalBatteryCharged))}</td>}
            </tr>
          </tbody>
        </Box>
      </Paper>

      {/* Daily Breakdown */}
      {report.dailyData.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 1, color: '#2c3e50' }}>
            <FormattedMessage id="dailyBreakdown" defaultMessage="Daily Breakdown" />
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, mb: 2, fontSize: '12px', color: '#666' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, bgcolor: '#f39c12', borderRadius: '2px' }} /> <FormattedMessage id="pvProduction" defaultMessage="PV Production" />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, bgcolor: '#3498db', borderRadius: '2px' }} /> <FormattedMessage id="consumption" defaultMessage="Consumption" />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, bgcolor: '#e74c3c', borderRadius: '2px' }} /> <FormattedMessage id="gridImport" defaultMessage="Grid Import" />
            </Box>
          </Box>
          {report.dailyData.map((d, i) => {
            const dt = new Date(d.date);
            const dayLabel = dt.toLocaleDateString(intl.locale, { weekday: 'short', month: 'short', day: 'numeric' });
            const isCurrentWeek = report.dailyData.length > 7 ? i >= report.dailyData.length - 7 : true;
            return (
              <Box key={d.date} sx={{ mb: 1.5, opacity: isCurrentWeek ? 1 : 0.6 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                  <Typography variant="caption" sx={{ fontWeight: isCurrentWeek ? 'bold' : 'normal', color: '#444', fontSize: '12px' }}>
                    {dayLabel}
                    {!isCurrentWeek && <span style={{ color: '#999', marginLeft: 4 }}><FormattedMessage id="prevWeek" defaultMessage="(prev week)" /></span>}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#888', fontSize: '11px' }}>
                    {intl.formatMessage({ id: 'pvProduction' })} {d.pvProduction.toFixed(1)} | {intl.formatMessage({ id: 'consumption' })} {d.loadConsumption.toFixed(1)} | {intl.formatMessage({ id: 'gridImport' })} {d.gridImport.toFixed(1)} kWh
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: '2px', height: 18 }}>
                  <Box sx={{ width: `${(d.pvProduction / maxDailyValue) * 100}%`, bgcolor: '#f39c12', borderRadius: '2px 0 0 2px', minWidth: d.pvProduction > 0 ? '2px' : 0, transition: 'width 0.3s' }} />
                  <Box sx={{ width: `${(d.loadConsumption / maxDailyValue) * 100}%`, bgcolor: '#3498db', minWidth: d.loadConsumption > 0 ? '2px' : 0, transition: 'width 0.3s' }} />
                  <Box sx={{ width: `${(d.gridImport / maxDailyValue) * 100}%`, bgcolor: '#e74c3c', borderRadius: '0 2px 2px 0', minWidth: d.gridImport > 0 ? '2px' : 0, transition: 'width 0.3s' }} />
                </Box>
              </Box>
            );
          })}
        </Paper>
      )}

      {/* Weekly History — weeks not yet captured in a monthly report, excluding the current week shown above */}
      <WeeklyHistory
        installationId={installationId}
        latestMonthlyPeriodEnd={latestMonthlyPeriodEnd}
        currentReportPeriodEnd={report?.periodEnd ?? null}
      />
    </>
  );
});

// ── Weekly History (saved weekly reports for current month) ─────

function WeeklyHistory({ installationId, latestMonthlyPeriodEnd, currentReportPeriodEnd }: {
  installationId: number;
  latestMonthlyPeriodEnd: string | null;
  currentReportPeriodEnd: string | null;
}) {
  const intl = useIntl();
  const [records, setRecords] = useState<WeeklyReportSummaryRecord[]>([]);

  useEffect(() => {
    const now   = new Date();
    const year  = now.getFullYear();
    const month = now.getMonth() + 1;
    const lang  = intl.locale;

    // Fetch current month and previous month to cover cross-month weeks
    const prevMonth = month === 1 ? 12 : month - 1;
    const prevYear  = month === 1 ? year - 1 : year;

    Promise.all([
      axiosConfig.get('/GetWeeklyReportSummaries', { params: { installationId, year, month, language: lang } })
        .then(r => r.data as WeeklyReportSummaryRecord[]).catch(() => [] as WeeklyReportSummaryRecord[]),
      axiosConfig.get('/GetWeeklyReportSummaries', { params: { installationId, year: prevYear, month: prevMonth, language: lang } })
        .then(r => r.data as WeeklyReportSummaryRecord[]).catch(() => [] as WeeklyReportSummaryRecord[])
    ]).then(([cur, prev]) => {
      // Combine, deduplicate by id, sort descending by periodEnd
      const all    = [...cur, ...prev];
      const unique = Array.from(new Map(all.map(r => [r.id, r])).values())
        .sort((a, b) => b.periodEnd.localeCompare(a.periodEnd));

      // Exclude the current week already shown as the main report above
      const withoutCurrent = currentReportPeriodEnd
        ? unique.filter(r => r.periodEnd !== currentReportPeriodEnd)
        : unique;

      // Keep only weeks not yet covered by a monthly report
      const uncovered = latestMonthlyPeriodEnd
        ? withoutCurrent.filter(r => r.periodEnd > latestMonthlyPeriodEnd)
        : withoutCurrent;

      // Always show at least the latest non-current week even if all are covered
      const toShow = uncovered.length > 0 ? uncovered : withoutCurrent.slice(0, 1);
      setRecords(toShow);
    });
  }, [installationId, latestMonthlyPeriodEnd, currentReportPeriodEnd, intl.locale]);

  if (records.length === 0) return null;

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: '#2c3e50' }}>
        <FormattedMessage id="recentWeeklyReports" defaultMessage="Recent Weekly Reports" />
      </Typography>
      {records.map((rec) => (
        <Accordion key={rec.id} sx={{ mb: 1, '&:before': { display: 'none' } }} disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: '#f8f9fa', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', pr: 1 }}>
              <Typography variant="body2" fontWeight="bold">
                {rec.periodStart} — {rec.periodEnd}
              </Typography>
              <Typography variant="caption" sx={{ color: '#888' }}>
                PV {rec.totalPvProduction.toFixed(1)} kWh | {intl.formatMessage({ id: 'consumption', defaultMessage: 'Consumption' })} {rec.totalConsumption.toFixed(1)} kWh
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2} sx={{ mb: 1 }}>
              <Grid item xs={6} sm={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body1" fontWeight="bold" color="#27ae60">{rec.totalEnergySaved.toFixed(1)} kWh</Typography>
                  <Typography variant="caption" color="#888"><FormattedMessage id="solarEnergyUsed" defaultMessage="Energy Saved" /></Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body1" fontWeight="bold" color="#2980b9">~{rec.totalSavingsCHF.toFixed(0)} CHF</Typography>
                  <Typography variant="caption" color="#888"><FormattedMessage id="estMoneySaved" defaultMessage="Est. Money Saved" /></Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body1" fontWeight="bold" color="#8e44ad">{rec.selfSufficiencyPercent.toFixed(0)}%</Typography>
                  <Typography variant="caption" color="#888"><FormattedMessage id="solarCoverage" defaultMessage="Energy Independence" /></Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body1" fontWeight="bold" color="#e67e22">{rec.batteryEfficiencyPercent.toFixed(0)}%</Typography>
                  <Typography variant="caption" color="#888"><FormattedMessage id="batteryEfficiency" defaultMessage="Battery Eff." /></Typography>
                </Box>
              </Grid>
            </Grid>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', '& td': { p: 0.8, borderBottom: '1px solid #f0f0f0' } }}>
              <tbody>
                <tr>
                  <td><FormattedMessage id="gridImport" defaultMessage="Grid Import" /></td>
                  <td style={{ textAlign: 'right' }}>{rec.totalGridImport.toFixed(1)} kWh</td>
                  <td style={{ width: 20 }} />
                  <td><FormattedMessage id="gridExport" defaultMessage="Grid Export" /></td>
                  <td style={{ textAlign: 'right' }}>{rec.totalGridExport.toFixed(1)} kWh</td>
                </tr>
                <tr>
                  <td><FormattedMessage id="batteryInOut" defaultMessage="Battery In / Out" /></td>
                  <td style={{ textAlign: 'right' }} colSpan={4}>{rec.totalBatteryCharged.toFixed(1)} / {rec.totalBatteryDischarged.toFixed(1)} kWh</td>
                </tr>
              </tbody>
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}
    </Paper>
  );
}

// ── Monthly Section ─────────────────────────────────────────────

function MonthlySection({
  installationId,
  reports,
  preview,
  selectedIdx,
  onSelectedIdxChange,
  onPeriodChange
}: {
  installationId: number;
  reports: MonthlyReport[];
  preview: MonthlyReport | null;
  selectedIdx: number;
  onSelectedIdxChange: (idx: number) => void;
  onPeriodChange?: (report: MonthlyReport) => void;
}) {
  const intl = useIntl();

  // Check if current month already has a finalized report
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  const hasFinalizedCurrentMonth = reports.some(r => r.year === currentYear && r.month === currentMonth);

  // Show preview only if no finalized report for current month exists
  const showPreview = preview && !hasFinalizedCurrentMonth;

  return (
    <>
      {/* Current month preview */}
      {showPreview && (
        <Paper sx={{ mb: 3, border: '1px solid #f0ad4e', borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ bgcolor: '#2c3e50', color: '#fff', p: 3, borderRadius: '8px 8px 0 0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography variant="h5" fontWeight="bold">
                <FormattedMessage id="monthlyReportTitle" defaultMessage="Monthly Performance Report" />
              </Typography>
              <Typography variant="caption" sx={{ bgcolor: '#f0ad4e', color: '#000', px: 1, py: 0.3, borderRadius: 1, fontWeight: 'bold' }}>
                <FormattedMessage id="reportInProgress" defaultMessage="{month} (in progress)" values={{ month: '' }} />
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              <FormattedMessage
                id="reportInProgress"
                defaultMessage="{month} (in progress)"
                values={{ month: `${getMonthName(preview.month, intl.locale)} ${preview.year}` }}
              />
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.6 }}>
              <FormattedMessage
                id="daysOfTotal"
                defaultMessage="{available} of {total} days"
                values={{ available: preview.daysAvailable ?? 0, total: preview.daysInMonth ?? 0 }}
              />
            </Typography>
          </Box>

          {/* Preview content — reuse InsightBox + SavingsCards */}
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: '#2c3e50' }}>
              <FormattedMessage id="monthlyInsights" defaultMessage="Monthly Insights" />
            </Typography>
            <InsightBox
              text={preview.aiInsight || ''}
              bullets={(preview.aiInsight || '').split(/\n+/).map(l => l.replace(/\*\*/g, '').replace(/^[\d]+[.)]\s*/, '').replace(/^[-*]\s*/, '').trim()).filter(l => l.length > 0)}
            />
          </Box>

          <Box sx={{ px: 3, pb: 2 }}>
            <SavingsCards
              intl={intl}
              energySaved={preview.totalEnergySaved}
              savingsCHF={preview.totalSavingsCHF}
              selfSufficiency={preview.selfSufficiencyPercent}
              batteryEfficiency={preview.batteryEfficiencyPercent}
            />
          </Box>

          <Alert severity="info" sx={{ mx: 3, mb: 3 }}>
            <FormattedMessage id="monthlyAutoNote" defaultMessage="Final report will be automatically generated on the 1st of next month." />
          </Alert>
        </Paper>
      )}

      {/* Existing monthly reports */}
      {reports.length > 0 ? (
        <AggregatedSection
          reports={reports}
          type="monthly"
          labelFn={(r: MonthlyReport) => `${getMonthName(r.month, intl.locale)} ${r.year}`}
          countLabelId="weeksAggregated"
          countFn={(r: MonthlyReport) => r.weekCount}
          sendEndpoint="/SendMonthlyReportEmail"
          sendParamsFn={(r: MonthlyReport) => ({ installationId, year: r.year, month: r.month })}
          controlledIdx={selectedIdx}
          onIdxChange={onSelectedIdxChange}
          onPeriodChange={onPeriodChange}
        />
      ) : !showPreview ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
          <Alert severity="warning">
            <FormattedMessage id="noReportData" defaultMessage="No report data found." />
          </Alert>
        </Box>
      ) : null}
    </>
  );
}

// ── Yearly Section ──────────────────────────────────────────────

function YearlySection({
  installationId,
  reports,
  preview,
  selectedIdx,
  onSelectedIdxChange,
  onPeriodChange
}: {
  installationId: number;
  reports: YearlyReport[];
  preview: YearlyReport | null;
  selectedIdx: number;
  onSelectedIdxChange: (idx: number) => void;
  onPeriodChange?: (report: YearlyReport) => void;
}) {
  const intl = useIntl();

  const currentYear = new Date().getFullYear();
  const hasFinalizedCurrentYear = reports.some(r => r.year === currentYear);
  const showPreview = preview && !hasFinalizedCurrentYear;

  return (
    <>
      {/* Current year preview */}
      {showPreview && (
        <Paper sx={{ mb: 3, border: '1px solid #f0ad4e', borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ bgcolor: '#2c3e50', color: '#fff', p: 3, borderRadius: '8px 8px 0 0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography variant="h5" fontWeight="bold">
                <FormattedMessage id="yearlyReportTitle" defaultMessage="Annual Performance Report" />
              </Typography>
              <Typography variant="caption" sx={{ bgcolor: '#f0ad4e', color: '#000', px: 1, py: 0.3, borderRadius: 1, fontWeight: 'bold' }}>
                <FormattedMessage id="reportInProgress" defaultMessage="{month} (in progress)" values={{ month: '' }} />
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              <FormattedMessage
                id="reportInProgress"
                defaultMessage="{month} (in progress)"
                values={{ month: `${preview.year}` }}
              />
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.6 }}>
              <FormattedMessage
                id="monthsOfTotal"
                defaultMessage="{available} of {total} months"
                values={{ available: preview.monthCount, total: 12 }}
              />
            </Typography>
          </Box>

          <Box sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: '#2c3e50' }}>
              <FormattedMessage id="yearlyInsights" defaultMessage="Annual Insights" />
            </Typography>
            <InsightBox
              text={preview.aiInsight || ''}
              bullets={(preview.aiInsight || '').split(/\n+/).map(l => l.replace(/\*\*/g, '').replace(/^[\d]+[.)]\s*/, '').replace(/^[-*]\s*/, '').trim()).filter(l => l.length > 0)}
            />
          </Box>

          <Box sx={{ px: 3, pb: 2 }}>
            <SavingsCards
              intl={intl}
              energySaved={preview.totalEnergySaved}
              savingsCHF={preview.totalSavingsCHF}
              selfSufficiency={preview.selfSufficiencyPercent}
              batteryEfficiency={preview.batteryEfficiencyPercent}
            />
          </Box>

          <Alert severity="info" sx={{ mx: 3, mb: 3 }}>
            <FormattedMessage id="yearlyAutoNote" defaultMessage="Final report will be automatically generated on January 2nd." />
          </Alert>
        </Paper>
      )}

      {/* Existing yearly reports */}
      {reports.length > 0 ? (
        <AggregatedSection
          reports={reports}
          type="yearly"
          labelFn={(r: YearlyReport) => `${r.year}`}
          countLabelId="monthsAggregated"
          countFn={(r: YearlyReport) => r.monthCount}
          sendEndpoint="/SendYearlyReportEmail"
          sendParamsFn={(r: YearlyReport) => ({ installationId, year: r.year })}
          controlledIdx={selectedIdx}
          onIdxChange={onSelectedIdxChange}
          onPeriodChange={onPeriodChange}
        />
      ) : !showPreview ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
          <Alert severity="warning">
            <FormattedMessage id="noReportData" defaultMessage="No report data found." />
          </Alert>
        </Box>
      ) : null}
    </>
  );
}

// ── Aggregated Section (Monthly / Yearly) ──────────────────────

function AggregatedSection<T extends ReportSummary>({
  reports,
  type,
  labelFn,
  countLabelId,
  countFn,
  sendEndpoint,
  sendParamsFn,
  controlledIdx,
  onIdxChange,
  onPeriodChange
}: {
  reports: T[];
  type: 'monthly' | 'yearly';
  labelFn: (r: T) => string;
  countLabelId: string;
  countFn: (r: T) => number;
  sendEndpoint: string;
  sendParamsFn: (r: T) => object;
  controlledIdx?: number;
  onIdxChange?: (idx: number) => void;
  onPeriodChange?: (report: T) => void;
}) {
  const intl = useIntl();
  const [internalIdx, setInternalIdx] = useState(0);
  const selectedIdx = controlledIdx ?? internalIdx;
  const handleIdxChange = (idx: number) => {
    setInternalIdx(idx);
    onIdxChange?.(idx);
    if (reports[idx]) onPeriodChange?.(reports[idx]);
  };

  // Report initial period on mount
  useEffect(() => {
    if (reports.length > 0 && reports[selectedIdx]) {
      onPeriodChange?.(reports[selectedIdx]);
    }
  }, [reports.length]);

  if (reports.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
        <Alert severity="warning">
          <FormattedMessage id="noReportData" defaultMessage="No report data found." />
        </Alert>
      </Box>
    );
  }

  const r = reports[selectedIdx];
  const insightsId = type === 'monthly' ? 'monthlyInsights' : 'yearlyInsights';
  const savingsId  = type === 'monthly' ? 'monthlySavings' : 'yearlySavings';
  const summaryId  = type === 'monthly' ? 'monthlySummary' : 'yearlySummary';
  const titleId    = type === 'monthly' ? 'monthlyReportTitle' : 'yearlyReportTitle';

  const insightBullets = r.aiInsight
    .split(/\n+/)
    .map((line) => line.replace(/\*\*/g, '').replace(/^[\d]+[.)]\s*/, '').replace(/^[-*]\s*/, '').trim())
    .filter((line) => line.length > 0);

  const handleSendEmail = async (emailAddress: string) => {
    await axiosConfig.post(sendEndpoint, null, {
      params: { ...sendParamsFn(r), emailAddress }
    });
  };

  return (
    <>
      {/* Period selector + Email bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        {reports.length > 1 && (
          <Select
            value={selectedIdx}
            onChange={(e) => handleIdxChange(Number(e.target.value))}
            size="small"
            sx={{ minWidth: 200 }}
          >
            {reports.map((rep, i) => (
              <MenuItem key={i} value={i}>{labelFn(rep)}</MenuItem>
            ))}
          </Select>
        )}
        <Box sx={{ ml: 'auto' }}>
          <EmailBar onSend={handleSendEmail} />
        </Box>
      </Box>

      {/* Header */}
      <Paper sx={{ bgcolor: '#2c3e50', color: '#fff', p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h5" fontWeight="bold">
          <FormattedMessage id={titleId} defaultMessage={type === 'monthly' ? 'Monthly Performance Report' : 'Annual Performance Report'} />
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.7 }}>
          {r.periodStart} — {r.periodEnd}
        </Typography>
        <Typography variant="caption" sx={{ opacity: 0.6 }}>
          <FormattedMessage id={countLabelId} defaultMessage="{count} periods aggregated" values={{ count: countFn(r) }} />
        </Typography>
      </Paper>

      {/* AI Insights */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: '#2c3e50' }}>
          <FormattedMessage id={insightsId} defaultMessage={type === 'monthly' ? 'Monthly Insights' : 'Annual Insights'} />
        </Typography>
        <InsightBox text={r.aiInsight} bullets={insightBullets} />
      </Paper>

      {/* Savings Cards */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: '#2c3e50' }}>
          <FormattedMessage id={savingsId} defaultMessage={type === 'monthly' ? 'Your Savings This Month' : 'Your Savings This Year'} />
        </Typography>
        <SavingsCards
          intl={intl}
          energySaved={r.totalEnergySaved}
          savingsCHF={r.totalSavingsCHF}
          selfSufficiency={r.selfSufficiencyPercent}
          batteryEfficiency={r.batteryEfficiencyPercent}
        />
      </Paper>

      {/* Summary Table */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: '#2c3e50' }}>
          <FormattedMessage id={summaryId} defaultMessage={type === 'monthly' ? 'Monthly Summary' : 'Annual Summary'} />
        </Typography>
        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', '& td, & th': { p: 1.2, borderBottom: '1px solid #eee' } }}>
          <thead>
            <tr style={{ background: '#f8f9fa' }}>
              <th style={{ textAlign: 'left' }}><FormattedMessage id="metric" defaultMessage="Metric" /></th>
              <th style={{ textAlign: 'right' }}><FormattedMessage id="total" defaultMessage="Total" /></th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><FormattedMessage id="pvProduction" defaultMessage="PV Production" /></td>
              <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{r.totalPvProduction.toFixed(1)} kWh</td>
            </tr>
            <tr>
              <td><FormattedMessage id="consumption" defaultMessage="Consumption" /></td>
              <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{r.totalConsumption.toFixed(1)} kWh</td>
            </tr>
            <tr>
              <td><FormattedMessage id="gridImport" defaultMessage="Grid Import" /></td>
              <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{r.totalGridImport.toFixed(1)} kWh</td>
            </tr>
            <tr>
              <td><FormattedMessage id="gridExport" defaultMessage="Grid Export" /></td>
              <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{r.totalGridExport.toFixed(1)} kWh</td>
            </tr>
            <tr>
              <td><FormattedMessage id="batteryInOut" defaultMessage="Battery Charge / Discharge" /></td>
              <td style={{ textAlign: 'right', fontWeight: 'bold' }}>{r.totalBatteryCharged.toFixed(1)} / {r.totalBatteryDischarged.toFixed(1)} kWh</td>
            </tr>
          </tbody>
        </Box>
      </Paper>
    </>
  );
}

// ── Shared Components ──────────────────────────────────────────

function InsightBox({ text, bullets }: { text: string; bullets: string[] }) {
  return (
    <Box
      sx={{
        bgcolor: '#fef9e7',
        borderLeft: '4px solid #f39c12',
        p: 2.5,
        borderRadius: '0 8px 8px 0'
      }}
    >
      {bullets.length > 1 ? (
        <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 1.2, lineHeight: 1.7, fontSize: '14px', color: '#333' } }}>
          {bullets.map((bullet, i) => (
            <li key={i}><FormattedBullet text={bullet} /></li>
          ))}
        </Box>
      ) : (
        <Typography sx={{ lineHeight: 1.7, fontSize: '14px', color: '#333' }}>
          <FormattedBullet text={text} />
        </Typography>
      )}
    </Box>
  );
}

export function SavingsCards({ intl, energySaved, savingsCHF, selfSufficiency, batteryEfficiency, hint }: {
  intl: any;
  energySaved: number;
  savingsCHF: number;
  selfSufficiency: number;
  batteryEfficiency: number;
  hint?: string;
}) {
  return (
    <Grid container spacing={2}>
      <Grid item xs={6} sm={3}>
        <SavingsCard
          label={intl.formatMessage({ id: 'solarEnergyUsed' })}
          value={`${energySaved} kWh`}
          subtitle={intl.formatMessage({ id: 'solarStayedHome' })}
          color="#27ae60"
          hint={hint}
        />
      </Grid>
      <Grid item xs={6} sm={3}>
        <SavingsCard
          label={intl.formatMessage({ id: 'estMoneySaved' })}
          value={`~${savingsCHF} CHF`}
          subtitle={intl.formatMessage({ id: 'atCHFRate' })}
          color="#2980b9"
        />
      </Grid>
      <Grid item xs={6} sm={3}>
        <SavingsCard
          label={intl.formatMessage({ id: 'solarCoverage' })}
          value={`${selfSufficiency.toFixed(0)}%`}
          subtitle={intl.formatMessage({ id: 'fromSolarSub' })}
          color="#8e44ad"
        />
      </Grid>
      <Grid item xs={6} sm={3}>
        <SavingsCard
          label={intl.formatMessage({ id: 'batteryEfficiency' })}
          value={`${batteryEfficiency.toFixed(0)}%`}
          subtitle={intl.formatMessage({ id: 'batteryEffSub' })}
          color="#e67e22"
        />
      </Grid>
    </Grid>
  );
}

function SavingsCard({ label, value, subtitle, color, hint }: { label: string; value: string; subtitle: string; color: string; hint?: string }) {
  return (
    <Box
      sx={{
        bgcolor: '#f8f9fa',
        borderRadius: 2,
        p: 2,
        textAlign: 'center'
      }}
    >
      <Typography variant="h4" fontWeight="bold" sx={{ color, fontSize: { xs: '1.5rem', sm: '2rem' } }}>
        {value}
      </Typography>
      <Typography variant="body2" fontWeight="bold" sx={{ color: '#444', mt: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="caption" sx={{ color: '#888' }}>
        {subtitle}
      </Typography>
      {hint && (
        <Typography variant="caption" sx={{ display: 'block', color, fontWeight: 'bold', mt: 0.5 }}>
          {hint}
        </Typography>
      )}
    </Box>
  );
}

export default WeeklyReport;
